import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompare } from '@contexts/CompareContext';

function FilledSlot({ product, onRemove }) {
  return (
    <div className="flex items-center gap-2 bg-n1 rounded px-2 py-1">
      {product.thumbnailUrl ? (
        <img
          src={product.thumbnailUrl}
          alt={product.description}
          className="w-10 h-10 rounded object-contain bg-n1"
        />
      ) : (
        <div className="w-10 h-10 rounded bg-n2 flex items-center justify-center">
          <svg className="w-5 h-5 text-n5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
      )}
      <span className="text-sm text-carbon-black font-sandvik-text truncate max-w-[150px]">
        {product.description}
      </span>
      <button
        onClick={() => onRemove(product.itemCode)}
        className="ml-1 text-n5 hover:text-carbon-black transition-colors"
        aria-label={`Remove ${product.description} from compare`}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function EmptySlot() {
  return (
    <div className="w-40 h-12 border-2 border-dashed border-n3 rounded flex items-center justify-center text-n5 text-sm">
      Add product
    </div>
  );
}

export default function StickyCompareBar() {
  const { compareList, removeFromCompare, clearCompare } = useCompare();
  const navigate = useNavigate();
  const [mobileExpanded, setMobileExpanded] = useState(false);

  if (compareList.length < 1) return null;

  const canCompare = compareList.length >= 2;

  const handleCompare = () => {
    if (canCompare) {
      navigate('/compare');
    }
  };

  // Build 3 slots (filled + empty)
  const slots = [];
  for (let i = 0; i < 3; i++) {
    if (compareList[i]) {
      slots.push(
        <FilledSlot key={compareList[i].itemCode} product={compareList[i]} onRemove={removeFromCompare} />
      );
    } else {
      slots.push(<EmptySlot key={`empty-${i}`} />);
    }
  }

  return (
    <>
      {/* Desktop layout */}
      <div className="hidden md:block fixed bottom-0 left-0 right-0 bg-white border-t-2 border-electric-blue shadow-lg z-40 px-6 py-3">
        <div className="flex items-center justify-between sandvik-container">
          <div className="flex items-center gap-4">
            {slots}
          </div>
          <div className="flex items-center">
            <button
              onClick={handleCompare}
              disabled={!canCompare}
              className={`bg-sandvik-blue text-white px-6 py-2 rounded font-sandvik-text font-medium transition-opacity ${
                !canCompare ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
              }`}
            >
              Compare ({compareList.length})
            </button>
            <button
              onClick={clearCompare}
              className="text-n6 hover:text-carbon-black text-sm font-sandvik-text cursor-pointer ml-3 transition-colors"
            >
              Clear all
            </button>
          </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div
        className={`md:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-electric-blue shadow-lg z-40 transition-transform duration-300 ${
          mobileExpanded ? 'translate-y-0' : ''
        }`}
      >
        {/* Collapsed header — always visible */}
        <button
          onClick={() => setMobileExpanded(!mobileExpanded)}
          className="w-full flex items-center justify-between p-3"
        >
          <span className="flex items-center gap-2 text-sm font-sandvik-text font-medium text-carbon-black">
            Compare ({compareList.length})
            <svg
              className={`w-4 h-4 transition-transform duration-300 ${mobileExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCompare();
            }}
            disabled={!canCompare}
            className={`bg-sandvik-blue text-white px-4 py-1.5 rounded text-sm font-sandvik-text font-medium transition-opacity ${
              !canCompare ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
            }`}
          >
            Compare
          </button>
        </button>

        {/* Expanded panel */}
        <div
          className={`overflow-hidden transition-all duration-300 ${
            mobileExpanded ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-3 pb-3">
            <div className="flex items-center gap-3 mb-3 overflow-x-auto">
              {compareList.map(product => (
                <div key={product.itemCode} className="flex items-center gap-1 shrink-0 bg-n1 rounded px-2 py-1">
                  {product.thumbnailUrl ? (
                    <img
                      src={product.thumbnailUrl}
                      alt={product.description}
                      className="w-8 h-8 rounded object-contain"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-n2 flex items-center justify-center">
                      <svg className="w-4 h-4 text-n5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                  )}
                  <span className="text-xs text-carbon-black font-sandvik-text truncate max-w-[100px]">
                    {product.description}
                  </span>
                  <button
                    onClick={() => removeFromCompare(product.itemCode)}
                    className="ml-1 text-n5 hover:text-carbon-black"
                    aria-label={`Remove ${product.description}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={clearCompare}
                className="text-n6 hover:text-carbon-black text-sm font-sandvik-text cursor-pointer transition-colors"
              >
                Clear all
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
